import { ShieldCheck, Users, Lightbulb, Truck, Handshake, Award, Target } from 'lucide-react'
import { useApp } from '../context/AppContext'
import SEO from '../components/SEO'

const STORY_STEPS = [
  { icon: <Handshake size={20} />, titleAr: 'الشحن البري المتخصص', descAr: 'نقل بري احترافي يربط مصر بكل محافظة ودولة مجاورة' },
  { icon: <Truck size={20} />, titleAr: 'أسطول شاحنات حديث', descAr: 'شاحنات مبردة وعادية مجهزة بأحدث أنظمة التتبع' },
  { icon: <ShieldCheck size={20} />, titleAr: 'أمان البضائع في الطريق', descAr: 'تغليف عالي الجودة وتأمين كامل لكل شحنة برية' },
  { icon: <Award size={20} />, titleAr: 'توصيل من الباب إلى الباب', descAr: 'وصول مباشر من مستودع العميل إلى وجهته النهائية' },
]

const VALUE_ITEMS = [
  { icon: <Award size={22} />, titleAr: 'خبرة برية عميقة', descAr: 'عقد من الخبرة في الطرق البرية ومعابر الحدود' },
  { icon: <Target size={22} />, titleAr: 'إدارة محكمة للشحنات', descAr: 'تتبع متقدم لكل شحنة برية أثناء الرحلة' },
  { icon: <Users size={22} />, titleAr: 'فريق سائقين وخبراء', descAr: 'سائقون محترفون وفريق تخليص جمركي بري' },
  { icon: <Lightbulb size={22} />, titleAr: 'حلول برية مخصصة', descAr: 'خدمات شحن برية تناسب كل حجم شحنة' },
]

export default function About() {
  const { lang } = useApp()
  const isAr = lang === 'ar'

  return (
    <main className="page about-page" dir={isAr ? 'rtl' : 'ltr'}>
      <SEO
        title={isAr ? 'من نحن - الشحن البري' : 'About Us - Land Freight'}
        description={isAr ? 'شليل للشحن البري, نقل بري موثوق وآمن بين محافظات مصر والدول المجاورة. أكثر من عقد من الخبرة في النقل البري.' : 'Shalil Land Freight, reliable and secure land transport between Egyptian governorates and neighboring countries.'}
        url="https://shelil-logistics-services.pages.dev/about"
      />

      {/* Hero */}
      <section className="hero-section">
        <div className="hero-bg">
          <div className="hero-overlay" />
        </div>
        <div className="hero-content">
          <p className="hero-tagline">
            {isAr ? 'الشحن البري' : 'Land Freight'}
          </p>
          <h1 className="hero-title">
            {isAr ? 'نقل بري موثوق من الباب إلى الباب' : 'Reliable Door-to-Door Land Freight'}
          </h1>
          <p className="hero-desc">{isAr
            ? 'الشحن البري إلى جميع محافظات مصر والدول المجاورة بأحدث أسطول شاحنات، مع تجهيز مبرد للبضائع الحساسة.'
            : 'Land freight to all Egyptian governorates and neighboring countries with a modern truck fleet. Refrigerated options for sensitive cargo.'
          }</p>
          <div className="hero-btns">
            <a href="/contact" className="btn btn-primary">{isAr ? 'اطلب شحنة الآن' : 'Request Freight Now'}</a>
          </div>
        </div>
      </section>

      {/* Vision */}
      <section className="section">
        <div className="section-inner">
          <div className="section-header">
            <h2>{isAr ? 'رؤيتنا' : 'Our Vision'}</h2>
            <p>{isAr ? 'نؤمن أن الشحن البري هو عصب التجارة في مصر والمنطقة. نحرك البضائع من الباب إلى الباب، بسرعة وأمان.' : 'Land freight drives trade across Egypt and the region. We move cargo door-to-door, fast and safe.'}</p>
          </div>
          <div className="services-grid">
            {STORY_STEPS.map((s, i) => (
              <div key={i} className="service-card anim-child">
                <div className="service-icon">{s.icon}</div>
                <h3>{s.titleAr}</h3>
                <p>{s.descAr}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="section" style={{ background: 'var(--bg-section)' }}>
        <div className="section-inner">
          <div className="section-header">
            <h2>{isAr ? 'أرقامنا' : 'By the Numbers'}</h2>
          </div>
          <div className="services-grid">
            {[
              { num: '10+', label: isAr ? 'سنة خبرة' : 'Years Experience' },
              { num: '500+', label: isAr ? 'عميل راضٍ' : 'Happy Clients' },
              { num: '10K+', label: isAr ? 'شحنة منتج' : 'Shipments Delivered' },
              { num: '50+', label: isAr ? 'مسار بري' : 'Land Routes' },
            ].map((item, i) => (
              <div key={i} className="service-card anim-child">
                <div style={{ fontSize: '40px', fontWeight: 800, color: 'var(--accent)', fontFamily: 'Georgia, serif', marginBottom: 12 }}>{item.num}</div>
                <h3 style={{ color: 'var(--primary)', marginBottom: 8 }}>{item.label}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="section">
        <div className="section-inner">
          <div className="section-header">
            <h2>{isAr ? 'قيمنا' : 'Our Values'}</h2>
            <p>{isAr ? 'نعمل بالجودة والموثوقية في كل شحنة.' : 'Quality and reliability in every shipment.'}</p>
          </div>
          <div className="services-grid">
            {VALUE_ITEMS.map((v, i) => (
              <div key={i} className="service-card anim-child">
                <div className="service-icon">{v.icon}</div>
                <h3>{v.titleAr}</h3>
                <p>{v.descAr}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta-section">
        <div className="cta-inner">
          <h2>{isAr ? 'هل أنت مستعد لنقل بضاعتك؟' : 'Ready to move your cargo?'}</h2>
          <p>{isAr ? 'تواصل معنا الآن واحصل على استشارة مجانية للشحن البري.' : 'Contact us now for a free land freight consultation.'}</p>
          <a href="/contact" className="btn btn-white">{isAr ? 'تواصل معنا الآن' : 'Contact Us Today'}</a>
        </div>
      </section>
    </main>
  )
}
